import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { ActivityTypeService } from '@/shared/api/services'
import type {
  ActivityTypeListResponse,
  ActivityTypeCreateRequest,
  ActivityTypeUpdateRequest,
  ActivityTypeResponse
} from '@/shared/api/types'

// === ACTIVITY TYPES HOOKS ===

/**
 * Получить список видов деятельности с пагинацией и фильтрами
 */
export const useActivityTypesList = (params?: {
  page?: number
  limit?: number
  search?: string
  include_inactive?: boolean
}) => {
  return useQuery({
    queryKey: ['activityTypes', 'list', params],
    queryFn: () => ActivityTypeService.getActivityTypesList(params),
    staleTime: 5 * 60 * 1000, // 5 минут
  })
}

/**
 * Создать новый вид деятельности
 */
export const useCreateActivityType = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (request: ActivityTypeCreateRequest) =>
      ActivityTypeService.createActivityType(request),
    onSuccess: (data: ActivityTypeResponse) => {
      // Инвалидируем кэш списка
      queryClient.invalidateQueries({ queryKey: ['activityTypes', 'list'] })
      toast.success(`Вид деятельности "${data.name}" успешно создан`)
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Ошибка при создании вида деятельности'
      if (!error?.alreadyToasted) {
        toast.error(message)
      }
    },
  })
}

/**
 * Обновить вид деятельности
 */
export const useUpdateActivityType = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: ActivityTypeUpdateRequest }) =>
      ActivityTypeService.updateActivityType(id, request),
    onSuccess: (data: ActivityTypeResponse) => {
      // Инвалидируем кэш списка
      queryClient.invalidateQueries({ queryKey: ['activityTypes', 'list'] })
      toast.success(`Вид деятельности "${data.name}" успешно обновлен`)
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Ошибка при обновлении вида деятельности'
      if (!error?.alreadyToasted) {
        toast.error(message)
      }
    },
  })
}

/**
 * Удалить вид деятельности
 */
export const useDeleteActivityType = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: number) => ActivityTypeService.deleteActivityType(id),
    onSuccess: (data) => {
      // Инвалидируем кэш списка
      queryClient.invalidateQueries({ queryKey: ['activityTypes', 'list'] })
      toast.success(data.message)
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Ошибка при удалении вида деятельности'
      if (!error?.alreadyToasted) {
        toast.error(message)
      }
    },
  })
}
